import './MembershipActionsTab.css';

import { useMemo, useState } from 'react';
import { ClipboardList, Hourglass, Loader, TrendingUp, Megaphone } from 'lucide-react';
import type { MembershipTypeRow } from '../api/membership.api';
import { resolveThemeToken } from '../themeTokens';
import {
  useExpiringMembers,
  useMembershipBroadcast,
  useRecentMembershipActivity,
  useUpgradeCandidates,
} from '../queries';

type MembershipActionsTabProps = {
  tiers: MembershipTypeRow[];
  totalMembers: number;
  memberCountByType: Map<string, number>;
};

const ACTION_LABELS: Record<string, string> = {
  ISSUED: 'Issued',
  RENEWED: 'Renewed',
  REVOKED: 'Revoked',
  EXPIRED: 'Expired',
  REINSTATED: 'Reinstated',
};

const timeAgo = (iso: string): string => {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(diff)) return '';
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const initials = (name: string): string => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return parts.length > 1
    ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
    : parts[0].slice(0, 2).toUpperCase();
};

const daysUntil = (iso: string): number => {
  if (!iso) return 0;
  const diff = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / 86_400_000));
};

const MembershipActionsTab = ({ tiers, totalMembers, memberCountByType }: MembershipActionsTabProps) => {
  const [selectedAudience, setSelectedAudience] = useState<string>('all');
  const [message, setMessage] = useState('');
  const [broadcastFeedback, setBroadcastFeedback] = useState('');
  const broadcastMutation = useMembershipBroadcast();
  const orderedTiers = useMemo(
    () => [...tiers].sort((left, right) => (left.tierLevel || 0) - (right.tierLevel || 0)),
    [tiers]
  );

  const recipientCount = selectedAudience === 'all'
    ? totalMembers
    : (memberCountByType.get(selectedAudience) || 0);

  const activityQuery = useRecentMembershipActivity();
  const expiringQuery = useExpiringMembers(30);
  const candidatesQuery = useUpgradeCandidates();

  const sendBroadcast = async () => {
    setBroadcastFeedback('');
    try {
      const response = await broadcastMutation.mutateAsync({
        audience: selectedAudience,
        message: message.trim(),
        channels: ['SMS'],
      });
      setMessage('');
      setBroadcastFeedback(`Queued push notification for ${response.queued} member${response.queued === 1 ? '' : 's'}.`);
    } catch {
      setBroadcastFeedback('Unable to queue this broadcast right now.');
    }
  };

  return (
    <section className="membership-actions">
      <article className="appointments-v2-card membership-actions__panel">
        <div className="appointments-v2-card-header membership-actions__panelHeader">
          <h3>
            <span className="membership-actions__panel-icon" aria-hidden="true"><ClipboardList size={16} /></span>
            Recent activity
          </h3>
          {activityQuery.isLoading && <Loader size={14} className="spin" />}
        </div>
        <div className="appointments-v2-card-body">
          {activityQuery.data && activityQuery.data.length > 0 ? (
            <ul className="membership-actions__event-list">
              {activityQuery.data.map((row, i) => (
                <li key={`${row.membershipUuid}-${i}`} className="membership-actions__event-row">
                  <span className="membership-actions__avatar">{initials(row.customerName || row.userUuid || '?')}</span>
                  <span className="membership-actions__event-body">
                    <span className="membership-actions__event-name">{row.customerName || row.userUuid}</span>
                    <span className="membership-actions__event-action">{ACTION_LABELS[row.action] || row.action} · {row.tierName}</span>
                  </span>
                  <span className="membership-actions__event-time">{timeAgo(row.createdAt)}</span>
                </li>
              ))}
            </ul>
          ) : !activityQuery.isLoading ? (
            <div className="membership-actions__empty-illustration">
              <svg width="80" height="80" viewBox="0 0 80 80" fill="none" aria-hidden="true">
                <rect x="16" y="20" width="48" height="8" rx="4" fill="currentColor" opacity="0.15" />
                <rect x="16" y="34" width="36" height="8" rx="4" fill="currentColor" opacity="0.1" />
                <rect x="16" y="48" width="42" height="8" rx="4" fill="currentColor" opacity="0.08" />
              </svg>
              <p className="appointments-v2-empty-copy">No recent activity yet</p>
            </div>
          ) : null}
        </div>
      </article>

      <article className="appointments-v2-card membership-actions__panel">
        <div className="appointments-v2-card-header membership-actions__panelHeader">
          <h3>
            <span className="membership-actions__panel-icon" aria-hidden="true"><Hourglass size={16} /></span>
            Points expiring soon
          </h3>
          {expiringQuery.isLoading
            ? <Loader size={14} className="spin" />
            : <span className="membership-actions__countBadge">{expiringQuery.data?.length ?? 0} at risk</span>
          }
        </div>
        <div className="appointments-v2-card-body">
          {expiringQuery.data && expiringQuery.data.length > 0 ? (
            <ul className="membership-actions__event-list">
              {expiringQuery.data.map((row, i) => (
                <li key={`${row.membershipUuid}-${i}`} className="membership-actions__event-row">
                  <span className="membership-actions__avatar">{initials(row.customerName || '?')}</span>
                  <span className="membership-actions__event-body">
                    <span className="membership-actions__event-name">{row.customerName || row.userUuid}</span>
                    <span className="membership-actions__event-action">{row.pointsAtRisk} pts · expires in {daysUntil(row.expiresAt)}d</span>
                  </span>
                </li>
              ))}
            </ul>
          ) : !expiringQuery.isLoading ? (
            <div className="membership-actions__empty-illustration">
              <svg width="80" height="80" viewBox="0 0 80 80" fill="none" aria-hidden="true">
                <circle cx="40" cy="40" r="20" stroke="currentColor" strokeWidth="2" opacity="0.15" />
                <path d="M40 28v14l8 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.2" />
              </svg>
              <p className="appointments-v2-empty-copy">No members with expiring points</p>
            </div>
          ) : null}
        </div>
      </article>

      <article className="appointments-v2-card membership-actions__panel">
        <div className="appointments-v2-card-header membership-actions__panelHeader">
          <h3>
            <span className="membership-actions__panel-icon" aria-hidden="true"><TrendingUp size={16} /></span>
            Upgrade opportunities
          </h3>
          {candidatesQuery.isLoading
            ? <Loader size={14} className="spin" />
            : <span className="membership-actions__countBadge">{candidatesQuery.data?.length ?? 0} near threshold</span>
          }
        </div>
        <div className="appointments-v2-card-body">
          {candidatesQuery.data && candidatesQuery.data.length > 0 ? (
            <ul className="membership-actions__event-list">
              {candidatesQuery.data.map((row, i) => (
                <li key={`${row.membershipUuid}-${i}`} className="membership-actions__event-row">
                  <span className="membership-actions__avatar">{initials(row.customerName || '?')}</span>
                  <span className="membership-actions__event-body">
                    <span className="membership-actions__event-name">{row.customerName || row.userUuid}</span>
                    <span className="membership-actions__event-action">{row.currentTierName} → {row.nextTierName} · {row.progressPercent}%</span>
                  </span>
                  <span className="membership-actions__event-time">{row.progressValue} {row.progressUnit}</span>
                </li>
              ))}
            </ul>
          ) : !candidatesQuery.isLoading ? (
            <div className="membership-actions__empty-illustration">
              <svg width="80" height="80" viewBox="0 0 80 80" fill="none" aria-hidden="true">
                <path d="M20 56l12-16 12 8 16-24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.15" />
                <circle cx="60" cy="24" r="4" fill="currentColor" opacity="0.2" />
              </svg>
              <p className="appointments-v2-empty-copy">No upgrade opportunities identified yet</p>
            </div>
          ) : null}
        </div>
      </article>

      <article className="appointments-v2-card membership-actions__panel">
        <div className="appointments-v2-card-header membership-actions__panelHeader">
          <h3>
            <span className="membership-actions__panel-icon" aria-hidden="true"><Megaphone size={16} /></span>
            Broadcast
          </h3>
          <span className="membership-actions__countBadge">{totalMembers} reachable</span>
        </div>
        <div className="appointments-v2-card-body membership-actions__broadcast">
          <div className="membership-actions__chips">
            <button
              type="button"
              className={`membership-actions__chip ${selectedAudience === 'all' ? 'is-active' : ''}`}
              onClick={() => setSelectedAudience('all')}
            >
              All ({totalMembers})
            </button>
            {orderedTiers.map((tier) => {
              const theme = resolveThemeToken(tier.themeColorToken);
              return (
                <button
                  key={tier.membershipTypeUuid}
                  type="button"
                  className={`membership-actions__chip ${selectedAudience === tier.membershipTypeUuid ? 'is-active' : ''}`}
                  onClick={() => setSelectedAudience(tier.membershipTypeUuid)}
                >
                  <span className="membership-actions__chip-dot" style={{ background: theme.swatchHex }} />
                  {tier.name} ({memberCountByType.get(tier.membershipTypeUuid) || 0})
                </button>
              );
            })}
          </div>
          <textarea
            className="input membership-actions__textarea"
            placeholder="Type your message..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
          <div className="membership-actions__broadcast-meta">
            <span>{message.length} characters</span>
            <span>{recipientCount} recipient{recipientCount !== 1 ? 's' : ''}</span>
          </div>
          {broadcastFeedback ? <p className="appointments-v2-empty-copy">{broadcastFeedback}</p> : null}
          <button
            type="button"
            className="btn primary"
            disabled={!message.trim() || recipientCount === 0 || broadcastMutation.isPending}
            onClick={() => void sendBroadcast()}
          >
            {broadcastMutation.isPending ? 'Queuing...' : 'Send'}
          </button>
        </div>
      </article>
    </section>
  );
};

export default MembershipActionsTab;
